import { Alert, AppShell, AppShellMain, AspectRatio, Box, Button, Flex, Group, Image, Overlay, Select, Text } from "@mantine/core"
import { useContext, useEffect, useState, useCallback } from "react"
import { NBAInsightsContext } from "../context/global-state"
import { useLocation, useNavigate } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import { NBAInsightsHeader } from "../components/Header/Header"
import { getPublicPlaylists, getUserPlaylists } from "../api/Playlists"
import { IconInfoCircle, IconVideo } from "@tabler/icons-react"
import Fuse from 'fuse.js';
import { useMediaQuery } from "@mantine/hooks"

const defaultThumbnail = "https://images.pexels.com/photos/8892/pexels-photo.jpg?auto=compress&cs=tinysrgb&dpr=1&w=300"
const Playlists: React.FC = () => {
  // Layout info
  // const pinned = useHeadroom({ fixedAt: 120 });
  const isSmallScreen = useMediaQuery('(max-width: 768px)');

  const { userPlaylists, publicPlaylists, setUserPlaylists, setPublicPlaylists } = useContext(NBAInsightsContext)
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0()
  const navigate = useNavigate()
  const location = useLocation()
  const [hoveredItem, setHoveredItem] = useState<number | null>(null)
  const [fetchError, setFetchError] = useState<boolean>(false)

  const handleSelectPlaylist = (playlistId: string) => {
    navigate(`/playlists/${playlistId}`)
  }

  const fetchPublicPlaylists = useCallback(async () => {
    const pubPlaylists = await getPublicPlaylists()
    if (pubPlaylists === null) {
      setFetchError(true)
    } else {
      setPublicPlaylists(pubPlaylists)
    }
  }, [setPublicPlaylists])

  const fetchUserData = useCallback(async () => {
    if (isAuthenticated && user) {
      try {
        const accessToken = await getAccessTokenSilently()
        const playlistList = await getUserPlaylists(accessToken)
        if (playlistList === null) {
          setFetchError(true)
        } else {
          setUserPlaylists(playlistList)
        }
      } catch {
        setFetchError(true)
      }
    }
  }, [getAccessTokenSilently, isAuthenticated, setUserPlaylists, user])

  useEffect(() => {
    fetchPublicPlaylists()
  }, [fetchPublicPlaylists])

  useEffect(() => {
    fetchUserData()
  }, [fetchUserData])

  const handleRetry = () => {
    setFetchError(false)
    fetchPublicPlaylists()
    fetchUserData()
  }

  const uniquePlaylists = userPlaylists.concat(publicPlaylists)
    .filter((playlist, index, arr) => 
      arr.findIndex(p => p.id === playlist.id) === index
    );

  return (
    <AppShell
      padding={"md"}
      header={{ height: 64 }}
    > 
      <NBAInsightsHeader />
      <AppShellMain>
        <Flex direction="column" w="100%" pl={16} pr={16}>
          {fetchError && (
            <Alert variant="light" color="red" mb="md" icon={<IconInfoCircle />}>
              <Group justify="space-between" align="center">
                <Text size="sm">Unable to load playlists. Please check your connection.</Text>
                <Button size="xs" variant="subtle" color="red" onClick={handleRetry}>
                  Retry
                </Button>
              </Group>
            </Alert>
          )}
          <Flex justify={"center"}>
            { publicPlaylists && <Select
              w={isSmallScreen ? "90%" : "35%"}
              radius={20}
              placeholder="Search by name or description"
              leftSection={<IconVideo size={15}/>}
              // data={[...userPlaylists, ...publicPlaylists].map(p => { return { group: p.isOwner ? "user" : "public", label: `${p.name} | ${p.description.slice(0,20)}...`, value: p.id } })}
              data={uniquePlaylists.map(p => { return { label: p.name, value: p.id, items: [p.description, p.name] } })}
              rightSection={<></>}
              nothingFoundMessage="Playlist not found..."
              filter={({ options, search }) => {
                const fuse = new Fuse(options, { keys: ['label', 'items'] });
                return search ? fuse.search(search).map((item) => item.item) : options;
              }}
              searchable
              onChange={(id: string | null) => id && handleSelectPlaylist(id)} 
            />}
          </Flex>
        { isAuthenticated && (
          <Box flex={1}>
            <Text fz="h3" fw={600} p={10}>User Playlists</Text>
            <Flex wrap="nowrap" direction="row" style={{ overflowX: "auto" }} gap={20}>
              {userPlaylists.map((playlist, index) => (
                <Box key={index} mr={6} p={10}
                  onClick={() => handleSelectPlaylist(playlist.id)} 
                  onMouseEnter={() => setHoveredItem(index)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <AspectRatio ratio={1080/720} pos={"relative"}>
                    <Image
                      src={playlist.videos[0]?.videoThumbnail || playlist.thumbnailUrl || defaultThumbnail}
                      alt={playlist.name}
                      style={{ borderRadius: 20, cursor: "default" }}
                      w={250}
                    />
                    { hoveredItem === index && <Overlay color="#000" backgroundOpacity={0.5} radius={20} />}
                  </AspectRatio>
                  <Text mt={5} ml={10}>{playlist.name}</Text>
                </Box>
              ))}
              <Box mr={6} p={10} onClick={() => navigate('/modals/playlist', { state: { previousLocation: location }})}>
                <AspectRatio ratio={1080/720}>
                  <Flex
                    w={250}
                    justify={"center"}
                    align={"center"}
                    style={{ borderRadius: 20, cursor: "default" }}
                    bg={"#1f1f1f"}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(169,169,169,0.2)")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1f1f1f")}
                  >
                    <Text ta={"center"} mb={5}>+ New Playlist</Text>
                  </Flex>
                </AspectRatio>
              </Box>
            </Flex>
          </Box>
        )}
          <Box flex={1} mt={10}>
            <Text fz="h3" fw={600} p={10}>Public Playlists</Text>
            <Flex wrap="nowrap" direction="row" style={{ overflowX: "auto" }} >
              {publicPlaylists.map((playlist, index) => (
                <Box key={index} mr={6} p={10} onClick={() => handleSelectPlaylist(playlist.id)}>
                  <AspectRatio ratio={1080/720} >
                    <Image
                      src={playlist.videos[0]?.videoThumbnail || playlist.thumbnailUrl || defaultThumbnail}
                      alt={playlist.name}
                      style={{ borderRadius: 20, cursor: "default" }}
                      w={250}
                    />
                  </AspectRatio>
                  <Text mt={5} ml={10}>{playlist.name}</Text>
                </Box>
              ))}
              { publicPlaylists.length === 0 && <Box mr={6} p={10}>
                <AspectRatio ratio={1080/720}>
                  <Flex
                    w={250}
                    justify={"center"}
                    align={"center"}
                    style={{ borderRadius: 20, cursor: "default" }}
                    bg={"#1f1f1f"}
                  >
                    <Text ta={"center"} mb={5}>No Public Playlists</Text>
                  </Flex>
                </AspectRatio>
              </Box>}
            </Flex>
          </Box>
        </Flex>
      </AppShellMain>
    </AppShell>
  )
}

export default Playlists